/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
// import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { Node, Button, Collapse, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ICartViewProps } from '@msdyn365-commerce-modules/cart';
import { ICartlinesViewProps, ICartResources } from '@msdyn365-commerce-modules/cart/dist/types/index';
import { ICartProps } from '../definition-extensions/cart.ext.props.autogenerated';
import { PriceComponent } from '@msdyn365-commerce/components';
import MsDyn365, { getUrlSync, ICoreContext } from '@msdyn365-commerce/core';
import { CartLine } from '@msdyn365-commerce/retail-proxy';

export interface ICartViewState {
    isCartEmpty: boolean;
    isOpen: boolean;
    isOpenDelivery: boolean;
}

/**
 *
 * Cart component
 * @extends {React.PureComponent<ICartViewProps & ICartProps<{}>>}
 */
export class CartView extends React.PureComponent<ICartViewProps & ICartProps<{}>, ICartViewState> {
    constructor(props: ICartViewProps & ICartProps<{}>, state: ICartViewState) {
        super(props);
        this.state = {
            isCartEmpty: true,
            isOpen: false,
            isOpenDelivery: false
        };
    }

    public render(): JSX.Element {
        const {
            config,
            data,
            cartlines,
            context,
            title,
            resources,
            storeSelector,
            backToShoppingButton,
            waitingComponent,
            cartLoadingStatus,
            cartDataResult,
            orderSummaryHeading
        } = this.props;
        const {
            standardDeliveryPrice,
            expressDeliveryPrice,
            showfreeShippingPrompt,
            homeDeliveryText = '',
            standardDeliveryText = '',
            expressDeliveryText = '',
            preFreeShippingDiscountText,
            postFreeShippingDiscountText,
            qualifyForFreeDeliveryAmount
        } = config;
        data.products && data.products.result && cartlines && cartlines.length === 0
            ? this.setState({ isCartEmpty: true })
            : this.setState({ isCartEmpty: false });
        const cartAmount =
            data.cart && data.cart.result && data.cart.result.cart && data.cart.result.cart.AmountDue ? data.cart.result.cart.AmountDue : 0;
        /* START VSI Customization - {Add configurable qualify for free delivery amount} */
        const freeDeliveryAmount = qualifyForFreeDeliveryAmount && qualifyForFreeDeliveryAmount > 0 ? qualifyForFreeDeliveryAmount : 50;
        /* END VSI Customization - {Add configurable qualify for free delivery amount} */
        const cartAmountDue =
            freeDeliveryAmount - cartAmount > 0 ? context.cultureFormatter.formatCurrency(freeDeliveryAmount - cartAmount) : '';
        return (
            <div className='emptydiv'>
                {
                    <div className='hidden-md mobile-sticky-cart'>
                        <div className='row'>
                            <div className='col-sm-6'>
                                <div className='pricing-summary'>
                                    <p>
                                        <strong>{'Total'}</strong>
                                        {'(excluding delivery)'}
                                        <PriceComponent
                                            data={{
                                                price: {
                                                    CustomerContextualPrice: data.cart.result && data.cart.result.cart.AmountDue,
                                                    BasePrice: data.cart.result && data.cart.result.cart.AmountDue
                                                }
                                            }}
                                            context={this.props.context}
                                            id={this.props.id}
                                            typeName={this.props.typeName}
                                            className='msc-cart-line__product-savings-actual price-bold'
                                        />
                                    </p>
                                </div>
                            </div>
                            <div className='col-sm-6'>{this.checkoutAsSignedInUserBtn()}</div>
                        </div>
                    </div>
                }
                <div className={this.props.className} id={this.props.id} {...this.props.renderModuleAttributes(this.props)}>
                    <div className='col-lg-7'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='cart-item-wrap'>
                                    <div className='cart-head visible-xs-only'>{title}</div>
                                    <div className='msc-cartline-header'>
                                        <div className='row'>
                                            <div className='col-sm-8'>
                                                <div className='cart-head'>{title}</div>
                                            </div>
                                            <div className='col-sm-2 text-center'>
                                                <h3> Qty </h3>
                                            </div>
                                            <div className='col-sm-2 text-right'>
                                                <h3> Subtotal </h3>
                                            </div>
                                        </div>
                                    </div>
                                    <Node {...this.props.CartlinesWrapper}>
                                        {this._renderCartlines(
                                            cartlines,
                                            resources.emptyCartText,
                                            this.props.CartlinesWrapper,
                                            storeSelector,
                                            backToShoppingButton,
                                            waitingComponent,
                                            cartLoadingStatus,
                                            cartDataResult,
                                            resources,
                                            context
                                        )}
                                    </Node>
                                </div>
                            </div>
                            {storeSelector}
                            <div className='col-12'>
                                <div className='delivery-wrap'>
                                    <Button onClick={this.handleClickDelivery}>{homeDeliveryText}</Button>
                                    <div className='col-12'>
                                        <ul className='delivery-option'>
                                            <li className='delivery-info' key={1} role='button' style={{ listStyleType: 'none' }}>
                                                <label className='standard-delivery'>{standardDeliveryText}</label>
                                                <span className='delivery-price'>{standardDeliveryPrice}</span>
                                            </li>
                                            <li className='delivery-info' key={2} role='button' style={{ listStyleType: 'none' }}>
                                                <label className='exp-delivery'>{expressDeliveryText}</label>
                                                <span className='delivery-price'>{expressDeliveryPrice}</span>
                                            </li>
                                            {showfreeShippingPrompt && cartAmountDue && (
                                                <li className='delivery-info' key={3} style={{ listStyleType: 'none' }}>
                                                    <label className='info-text'>
                                                        {preFreeShippingDiscountText} {cartAmountDue} {postFreeShippingDiscountText}
                                                    </label>
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {orderSummaryHeading && (
                        <div className='col-lg-5'>
                            <div className='row'>
                                <div className='col-lg-10 col-xs-12 offset-lg-2'>
                                    <Node {...this.props.OrderSummaryWrapper}>
                                        <h2>Summary</h2>
                                        <div className='pricing-summary'>
                                            <p>
                                                <strong>{'Total'}</strong>
                                                {'(excluding delivery)'}
                                                <PriceComponent
                                                    data={{
                                                        price: {
                                                            CustomerContextualPrice: data.cart.result && data.cart.result.cart.AmountDue,
                                                            BasePrice: data.cart.result && data.cart.result.cart.AmountDue
                                                        }
                                                    }}
                                                    context={this.props.context}
                                                    id={this.props.id}
                                                    typeName={this.props.typeName}
                                                    className='msc-cart-line__product-savings-actual price-bold'
                                                />
                                            </p>
                                        </div>

                                        <div className='btn-box'>
                                            {this.checkoutAsSignedInUserBtn()}
                                            {this.checkoutAsGuestBtn()}
                                        </div>
                                        <div className='col-12'>
                                            <div className='promo-wrap'>
                                                <Button
                                                    className={
                                                        this.state.isOpen
                                                            ? 'add-code ms-refine-submenu__toggle_expanded'
                                                            : 'add-code ms-refine-submenu__toggle_collapsed'
                                                    }
                                                    onClick={this.handleClick}
                                                >
                                                    Add a promotion code
                                                </Button>
                                                <Collapse isOpen={this.state.isOpen} timeout={50}>
                                                    {this.props.promoCode}
                                                </Collapse>
                                            </div>
                                        </div>
                                    </Node>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className='tempDiv'>
                    <div className='basket-order-summary'>
                        <div className='row'>
                            <div className='col-5'>
                                <span role='button' className='continue-btn' onClick={this.goBack}>
                                    Continue Shopping
                                </span>
                            </div>
                            <div className='col-7'>
                                <div className='card'>
                                    <ul className='card-list'>
                                        <li className='mands-card'>
                                            <span className='american-exp-icon' />
                                        </li>
                                        <li className='master-card'>
                                            <span className='master-card-icon' />
                                        </li>
                                        <li className='visa-card'>
                                            <span className='visa-card-icon' />
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public getSitePath = (sitePath: string | undefined) => {
        return sitePath !== null ? sitePath : '';
    };

    public _renderCartlines = (
        cartLines: ICartlinesViewProps[] | undefined,
        cartEmptyText: string,
        CartlinesWrapper: INodeProps,
        storeSelector: React.ReactNode | undefined,
        backToShoppingButton: React.ReactNode,
        waitingComponent: React.ReactNode,
        cartLoadingStatus: string,
        cartDataResult: boolean,
        resources: ICartResources,
        context: ICoreContext
    ): JSX.Element[] | JSX.Element => {
        const sitePath = this.getSitePath(context.request.sitePath);
        const homeUrl = `${context.request.url.requestUrl.origin}${sitePath}/`;
        if (cartLoadingStatus) {
            return <>{cartLoadingStatus}</>;
        }
        if (cartLines) {
            return cartLines.map((cartLine, index) => {
                // @ts-ignore
                const cartProduct = this.props.data.cart.result && this.props.data.cart.result._cart.CartLines[index];
                const removeClickHandler = () => {
                    return this._removeItemFromCartHandler(cartProduct);
                };
                return (
                    <div className='msc-cart-lines-item' key={index}>
                        {cartLine.cartline}
                        {storeSelector
                            ? cartLine.pickUpInStore && (
                                  <Node {...cartLine.pickUpInStore.ContainerProps}>{cartLine.pickUpInStore.defaultComponent}</Node>
                              )
                            : null}
                        <div className='btn-box'>
                            {!this.state.isCartEmpty && cartLine.cartline ? (
                                <Button type='button' className='msc-cart-line__remove-item msc-btn' onClick={removeClickHandler}>
                                    {this.props.resources.removeCartButtonText}
                                </Button>
                            ) : null}
                            {cartLine.addToWishlist}
                        </div>
                    </div>
                );
            });
        } else {
            return cartDataResult ? (
                <div className='msc-cart__empty-cart'>
                    <p className='msc-cart-line'>{cartEmptyText}</p>
                    <Button className={'msc-cart__btn-backtoshopping'} href={homeUrl}>
                        {resources.backToShoppingButtonTitle}
                    </Button>
                </div>
            ) : (
                <>{waitingComponent}</>
            );
        }
    };

    public isCartLine(cartlines: ICartlinesViewProps[] | undefined) {
        let cartLine: boolean = false;
        cartLine = cartlines && cartlines.length ? true : false;
        return cartLine;
    }

    private goBack = () => {
        window.history.back();
    };

    private _removeItemFromCartHandler = (cartlineToRemove: CartLine) => {
        const { data, telemetry } = this.props;
        if (data.cart.result) {
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            data.cart.result.removeCartLines(input).catch(error => {
                telemetry.warning(error);
                telemetry.debug('Unable to Remove Cart Line');
            });
        }
    };
    private handleClickDelivery = () => {
        this.setState({ isOpenDelivery: !this.state.isOpenDelivery });
    };
    private handleClick = () => {
        this.setState({ isOpen: !this.state.isOpen });
    };
    private getCheckoutURL = (isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string): string => {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }

        const absoluteUrlRegExp = new RegExp('^(?:[a-z]+:)?//', 'i');
        const isAbsoluteUrl = absoluteUrlRegExp.test(guestCheckoutUrl);
        let returnURL = guestCheckoutUrl;
        /* START VSI Customization - {Change js window to MsDyn365.isBrowser} */
        if (MsDyn365.isBrowser && !isAbsoluteUrl) {
            returnURL = `${window.location.origin}${guestCheckoutUrl}`;
        }
        /* END VSI Customization - {Change js window to MsDyn365.isBrowser} */
        return `${signInUrl}${signInUrl.indexOf('?') === -1 ? '?' : '&'}ru=${returnURL}`;
    };
    private checkoutAsSignedInUserBtn = () => {
        const {
            context: {
                actionContext,
                request: {
                    user: { isAuthenticated, signInUrl }
                }
            }
        } = this.props;
        const guestCheckoutUrl = getUrlSync('checkout', actionContext) || '';
        const checkoutURL = this.getCheckoutURL(isAuthenticated, guestCheckoutUrl, signInUrl || '');
        return !this.state.isCartEmpty ? (
            <Button className={'msc-cart__btn-checkout'} href={checkoutURL}>
                {this.props.resources.checkoutButtonTitle}
            </Button>
        ) : null;
    };
    private checkoutAsGuestBtn = () => {
        const {
            context: {
                actionContext,
                request: {
                    user: { isAuthenticated }
                }
            }
        } = this.props;
        const guestCheckoutUrl = getUrlSync('checkout', actionContext) || '';
        return !this.state.isCartEmpty && !isAuthenticated ? (
            <Button className={'msc-cart__btn-guestcheckout'} href={guestCheckoutUrl}>
                {this.props.resources.guestCheckoutButtonTitle}
            </Button>
        ) : null;
    };
}
export default CartView;
